import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store"; // adjust path
import { AdminServices } from "../../../../../services/admin/AdminServices";

interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip: string | null;
  locality: string | null;
  lat: string | null;
  lng: string | null;
}

interface StoreResponse {
  storeName: string;
  address: Address;
  openingHourInMinutes: number;
  closingHourInMinutes: number;
  category: string;
  subCategory: string;
  socialIndicator: string;
  persona: string | null;
  incomeCategories: string | null;
  gender: string;
  avgMonthlyFootfall: number | null;
  ageGroups: string | null;
  notAllowedCategories: string | null;
  notAllowedSubcategories: string | null;
}

const StoreInfo: React.FC = () => {
  const selectedStoreId = useSelector(
    (state: RootState) => state.user.selectedStoreId
  );

  const [storeData, setStoreData] = useState<StoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddress, setShowAddress] = useState(false);

  useEffect(() => {
    if (!selectedStoreId) return;

    const fetchStore = async () => {
      try {
        setLoading(true);
        const { response } = await AdminServices.getStoreInfo(selectedStoreId);
        setStoreData(response);
      } catch (error) {
        console.error("Failed to fetch store info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [selectedStoreId]);

  const displayValue = (value: any) =>
    value !== null && value !== undefined && value !== "" ? value : "-";

  const formatTime = (minutes: number) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="text-gray-500 h-[10vh] flex justify-center items-center">
        Loading Store Info...
      </div>
    );
  }

  if (!storeData) {
    return (
      <div className="text-red-500 h-[10vh] flex justify-center items-center">
        Store data not available
      </div>
    );
  }

  return (
    <div className="border rounded-lg shadow-md p-6 w-full max-w-3xl mx-auto bg-white">
      <h2 className="text-lg font-bold mb-4">Store Information</h2>
      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr className="border-b">
            <td className="font-semibold p-2 w-1/3">Store Name</td>
            <td className="p-2">{displayValue(storeData.storeName)}</td>
          </tr>

          <tr className="border-b">
            <td className="font-semibold p-2">Category</td>
            <td className="p-2">{displayValue(storeData.category)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Sub Category</td>
            <td className="p-2">{displayValue(storeData.subCategory)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Social Indicator</td>
            <td className="p-2">{displayValue(storeData.socialIndicator)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Persona</td>
            <td className="p-2">{displayValue(storeData.persona)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Income Categories</td>
            <td className="p-2">{displayValue(storeData.incomeCategories)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Gender</td>
            <td className="p-2">{displayValue(storeData.gender)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Avg Monthly Footfall</td>
            <td className="p-2">{displayValue(storeData.avgMonthlyFootfall)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Age Groups</td>
            <td className="p-2">{displayValue(storeData.ageGroups)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Not Allowed Categories</td>
            <td className="p-2">{displayValue(storeData.notAllowedCategories)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Not Allowed Subcategories</td>
            <td className="p-2">{displayValue(storeData.notAllowedSubcategories)}</td>
          </tr>

          {/* Collapsible Address */}
          <tr
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => setShowAddress((prev) => !prev)}
          >
            <td className="font-semibold p-2">Address</td>
            <td className="p-2 text-blue-600">
              {showAddress ? "Hide details ▲" : "Show details ▼"}
            </td>
          </tr>
          {showAddress && storeData.address && (
            <tr>
              <td colSpan={2} className="p-2 bg-gray-50">
                <table className="w-full text-sm border border-gray-200 rounded-md">
                  <tbody>
                    {Object.entries(storeData.address).map(([key, value]) => (
                      <tr key={key}>
                        <td className="font-semibold p-2 capitalize">{key}</td>
                        <td className="p-2">{displayValue(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </td>
            </tr>
          )}

          {/* Opening & Closing Hours */}
          <tr className="border-b">
            <td className="font-semibold p-2">Opening Hour</td>
            <td className="p-2">{formatTime(storeData.openingHourInMinutes)}</td>
          </tr>
          <tr className="border-b">
            <td className="font-semibold p-2">Closing Hour</td>
            <td className="p-2">{formatTime(storeData.closingHourInMinutes)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default StoreInfo;
